import React, { useState, useEffect } from "react";

const isAllBidderCategories = (categories, availableCategories) => {
  if (!categories || categories.length === 0) return true;
  if (!availableCategories || availableCategories.length === 0) return true;
  return (
    availableCategories.every((cat) => categories.includes(cat)) &&
    categories.every((cat) => availableCategories.includes(cat))
  );
};

const normalizeBidderCategories = (categories, availableCategories) => {
  if (!categories?.length) return [];
  if (isAllBidderCategories(categories, availableCategories)) return [];
  return categories.filter((c) => c !== "all");
};

const isBidderCategorySelected = (category, categories, availableCategories) =>
  isAllBidderCategories(categories, availableCategories) ||
  categories?.includes(category);
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import axios from "axios";
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from "../context/AuthContext";

// TabPanel defined outside Settings so it isn't recreated on each render (prevents password inputs losing focus)
const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 4 }}>{children}</Box>}
  </div>
);

const Settings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [teamlessUsers, setTeamlessUsers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedMaster, setSelectedMaster] = useState('');
  const [teamName, setTeamName] = useState('');
  const [showMasterDialog, setShowMasterDialog] = useState(false);
  const [pendingBidderId, setPendingBidderId] = useState(null);
  const [isEditingExistingBidder, setIsEditingExistingBidder] = useState(false);
  const [hasBidders, setHasBidders] = useState(false);
  const [myBidders, setMyBidders] = useState([]);
  const [showBidderSettings, setShowBidderSettings] = useState(false);
  const [bidderSettings, setBidderSettings] = useState({
    categories: [],
    locations: [],
    jobBehaviors: [],
    techStacks: []
  });
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableLocations] = useState(['USA', 'Canada', 'LATAM', 'Europe', 'Asia', 'Australia/Pacific', 'Africa', 'Anonymous']);
  const [availableJobBehaviors] = useState(['Remote Job', 'Remote/Hybrid', 'Non Remote Job', 'Email Found', 'Login Required', 'Verification Required', 'Other Relevant', 'Expired', 'Irrelevant', 'Uncategorized']);
  const [teamTechStacks, setTeamTechStacks] = useState([]);
  const [showTechStacksDialog, setShowTechStacksDialog] = useState(false);
  const [editingTechStacks, setEditingTechStacks] = useState([]);
  const [newTechStackLabel, setNewTechStackLabel] = useState('');
  const [newTechStackDescription, setNewTechStackDescription] = useState('');
  const [techStacksSaving, setTechStacksSaving] = useState(false);
  const [teamJobsiteKeywords, setTeamJobsiteKeywords] = useState([]);
  const [editingJobsiteKeywords, setEditingJobsiteKeywords] = useState([]);
  const [newJobsiteKeyword, setNewJobsiteKeyword] = useState('');
  const [jobsiteKeywordsSaving, setJobsiteKeywordsSaving] = useState(false);
  const [bidderSettingsLoading, setBidderSettingsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [hasCustomOpenaiKey, setHasCustomOpenaiKey] = useState(false);
  const [openaiKeyLoading, setOpenaiKeyLoading] = useState(false);
  
  // Individual bidder settings state
  const [showIndividualBidderSettings, setShowIndividualBidderSettings] = useState(false);
  const [selectedBidder, setSelectedBidder] = useState(null);
  const [individualBidderSettings, setIndividualBidderSettings] = useState({
    categories: [],
    locations: [],
    jobBehaviors: [],
    techStacks: []
  });
  const [individualBidderSettingsLoading, setIndividualBidderSettingsLoading] = useState(false);

  const defaultProxySettings = {
    proxies: [],
    selectedProxyId: null,
    proxyFunctionalityEnabled: false
  };
  const [proxySettings, setProxySettings] = useState(defaultProxySettings);
  const [proxySettingsLoading, setProxySettingsLoading] = useState(false);
  const [proxySettingsSaving, setProxySettingsSaving] = useState(false);
  const [newProxyForm, setNewProxyForm] = useState({
    title: '',
    host: '',
    port: '',
    type: 'http',
    username: '',
    password: ''
  });

  const isBidder = user?.role === 'bidder';
  const proxyTabIndex = 2;
  const teamTabIndex = 3;
  const jobsitesTabIndex = 4;
  const passwordTabIndex = isBidder ? 2 : (user?.role === 'lead' ? 5 : 3);
  const openaiTabIndex = 6;

  const bidderTechStackOptions = teamTechStacks.some((stack) => stack.label === 'N/A')
    ? teamTechStacks
    : [...teamTechStacks, { label: 'N/A', description: '' }];

  const fetchTeamTechStacks = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team-info`);
      setTeamTechStacks(response.data?.team?.techStacks || []);
      setTeamJobsiteKeywords(response.data?.team?.jobsiteKeywords || []);
    } catch (error) {
      if (error.response?.status !== 403 && error.response?.status !== 404) {
        console.error('Error fetching team tech stacks:', error);
      }
    }
  };

  useEffect(() => {
    fetchTeamData();
    checkForBidders();
    fetchAvailableCategories();
    fetchOpenaiApiKeyStatus();
    fetchTeamTechStacks();
    if (user?.role !== 'bidder') {
      fetchProxySettings();
    }
  }, []);

  useEffect(() => {
    setEditingJobsiteKeywords(teamJobsiteKeywords.map((keyword) => String(keyword).trim().toLowerCase()));
  }, [teamJobsiteKeywords]);

  const fetchOpenaiApiKeyStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/openai-api-key`);
      setHasCustomOpenaiKey(response.data.hasCustomKey);
      // Don't fetch the actual key for security - user will need to enter it if they want to update
    } catch (error) {
      // If user doesn't have permission or not in team, silently fail
      if (error.response?.status !== 403 && error.response?.status !== 404) {
        console.error('Error fetching OpenAI API key status:', error);
      }
    }
  };

  const fetchProxySettings = async () => {
    setProxySettingsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/proxy-settings`);
      setProxySettings(response.data || defaultProxySettings);
    } catch (error) {
      console.error('Error fetching proxy settings:', error);
      toast.error(error.response?.data?.message || 'Failed to load proxy settings');
    } finally {
      setProxySettingsLoading(false);
    }
  };

  const saveProxiesToServer = async (proxies, selectedProxyId, proxyFunctionalityEnabled) => {
    const payload = { proxies, selectedProxyId };
    if (proxyFunctionalityEnabled !== undefined) {
      payload.proxyFunctionalityEnabled = proxyFunctionalityEnabled;
    }
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/users/proxy-settings`, payload);
    setProxySettings((prev) => ({
      ...prev,
      ...(response.data.settings || { proxies, selectedProxyId })
    }));
    return response.data.settings;
  };

  const handleProxyFunctionalityToggle = async (enabled) => {
    setProxySettingsSaving(true);
    try {
      await saveProxiesToServer(
        proxySettings.proxies,
        proxySettings.selectedProxyId,
        enabled
      );
      setProxySettings((prev) => ({ ...prev, proxyFunctionalityEnabled: enabled }));
      toast.success(enabled ? 'Proxy functionality enabled' : 'Proxy functionality disabled for you and your bidders');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update proxy settings');
    } finally {
      setProxySettingsSaving(false);
    }
  };

  const handleAddProxy = async () => {
    const { title, host, port, username, password } = newProxyForm;
    if (!title.trim() || !host.trim() || !port) {
      toast.error('Proxy title, host, and port are required');
      return;
    }

    const portNumber = Number(port);
    if (!Number.isInteger(portNumber) || portNumber <= 0 || portNumber > 65535) {
      toast.error('Port must be a valid number between 1 and 65535');
      return;
    }

    const newProxy = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      title: title.trim(),
      host: host.trim(),
      port: portNumber,
      type: 'http',
      username: username.trim(),
      password
    };

    const updatedProxies = [...proxySettings.proxies, newProxy];
    const selectedProxyId = proxySettings.selectedProxyId || newProxy.id;

    setProxySettingsSaving(true);
    try {
      await saveProxiesToServer(updatedProxies, selectedProxyId);
      setNewProxyForm({ title: '', host: '', port: '', type: 'http', username: '', password: '' });
      toast.success('Proxy added');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add proxy');
    } finally {
      setProxySettingsSaving(false);
    }
  };

  const handleRemoveProxy = async (proxyId) => {
    const proxies = proxySettings.proxies.filter((proxy) => proxy.id !== proxyId);
    const selectedProxyId =
      proxySettings.selectedProxyId === proxyId
        ? (proxies[0]?.id || null)
        : proxySettings.selectedProxyId;

    setProxySettingsSaving(true);
    try {
      await saveProxiesToServer(proxies, selectedProxyId);
      toast.success('Proxy removed');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove proxy');
    } finally {
      setProxySettingsSaving(false);
    }
  };

  const handleDefaultProxyChange = async (proxyId) => {
    setProxySettingsSaving(true);
    try {
      await saveProxiesToServer(proxySettings.proxies, proxyId);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update default proxy');
      fetchProxySettings();
    } finally {
      setProxySettingsSaving(false);
    }
  };

  const handleSaveOpenaiApiKey = async () => {
    setOpenaiKeyLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/teams/openai-api-key`, {
        openaiApiKey: openaiApiKey.trim()
      });
      setHasCustomOpenaiKey(!!openaiApiKey.trim());
      setOpenaiApiKey(''); // Clear the input after saving
      toast.success(openaiApiKey.trim() ? "OpenAI API key saved successfully" : "OpenAI API key removed, using global key");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save OpenAI API key");
    } finally {
      setOpenaiKeyLoading(false);
    }
  };

  const checkForBidders = async () => {
    try {
      if (user.role === 'bidder') {
        setHasBidders(false);
        setMyBidders([]);
        return;
      }
      
      const teamResponse = await axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team`);
      const teamData = teamResponse.data;
      
      const myBiddersList = teamData.filter(member => {
        if (!member.master) {
          return false;
        }
        
        // Handle both populated (object) and unpopulated (string) master field
        const masterId = typeof member.master === 'object' 
          ? member.master._id?.toString() 
          : member.master.toString();
        
        const userId = user._id.toString();
        return masterId === userId;
      });
      
      setMyBidders(myBiddersList);
      setHasBidders(myBiddersList.length > 0);
    } catch (error) {
      console.error('Error checking for bidders:', error);
    }
  };

  const fetchAvailableCategories = async () => {
    try {
      // Fetch categories from team collection
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/categories`);
      const categories = response.data.categories || [];
      setAvailableCategories(categories.sort());
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error("Failed to fetch categories");
    }
  };

  const fetchBidderSettings = async () => {
    try {
      setBidderSettingsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/bidder-settings`);
      setBidderSettings({
        ...response.data,
        categories: normalizeBidderCategories(
          response.data.categories,
          availableCategories
        ),
      });
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error(error.response?.data?.message || "Failed to fetch bidder settings");
      }
    } finally {
      setBidderSettingsLoading(false);
    }
  };

  const handleBidderSettingsClick = () => {
    setShowBidderSettings(true);
    fetchBidderSettings();
    fetchTeamTechStacks();
  };

  const handleOpenTechStacksDialog = () => {
    setEditingTechStacks(teamTechStacks.map(s => ({ label: s.label || '', description: s.description || '' })));
    setNewTechStackLabel('');
    setNewTechStackDescription('');
    setShowTechStacksDialog(true);
  };

  const handleAddTechStack = () => {
    const label = newTechStackLabel.trim();
    if (!label) return;
    setEditingTechStacks(prev => [...prev, { label, description: newTechStackDescription.trim() }]);
    setNewTechStackLabel('');
    setNewTechStackDescription('');
  };

  const handleRemoveTechStack = (index) => {
    setEditingTechStacks(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateTechStack = (index, field, value) => {
    setEditingTechStacks(prev =>
      prev.map((s, i) => {
        if (i !== index) return s;
        return {
          ...s,
          [field]: value
        };
      })
    );
  };

  const handleSaveTechStacks = async () => {
    try {
      setTechStacksSaving(true);

      const cleaned = editingTechStacks
        .filter(s => s && s.label && String(s.label).trim())
        .map(s => ({
          label: String(s.label).trim(),
          description: typeof s.description === 'string' ? s.description.trim() : ''
        }));

      const normalizedLabels = cleaned.map(s => s.label.toLowerCase());
      const duplicates = normalizedLabels.filter((l, idx) => normalizedLabels.indexOf(l) !== idx);
      if (duplicates.length > 0) {
        toast.error('Tech stack labels must be unique (duplicates found).');
        return;
      }

      await axios.put(`${process.env.REACT_APP_API_URL}/teams/tech-stacks`, {
        techStacks: cleaned
      });
      await fetchTeamTechStacks();
      setShowTechStacksDialog(false);
      toast.success('Tech stacks saved. New jobs will be classified using these labels.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save tech stacks');
    } finally {
      setTechStacksSaving(false);
    }
  };

  const handleAddJobsiteKeyword = () => {
    const keyword = newJobsiteKeyword.trim().toLowerCase();
    if (!keyword) return;
    if (editingJobsiteKeywords.includes(keyword)) {
      toast.error('Keyword already added.');
      return;
    }
    setEditingJobsiteKeywords((prev) => [...prev, keyword]);
    setNewJobsiteKeyword('');
  };

  const handleRemoveJobsiteKeyword = (index) => {
    setEditingJobsiteKeywords((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateJobsiteKeyword = (index, value) => {
    setEditingJobsiteKeywords((prev) =>
      prev.map((keyword, i) => (i === index ? value : keyword))
    );
  };

  const handleSaveJobsiteKeywords = async () => {
    try {
      setJobsiteKeywordsSaving(true);

      const cleaned = editingJobsiteKeywords
        .map((keyword) => String(keyword).trim().toLowerCase())
        .filter(Boolean);

      const duplicates = cleaned.filter((keyword, idx) => cleaned.indexOf(keyword) !== idx);
      if (duplicates.length > 0) {
        toast.error('Jobsite keywords must be unique (duplicates found).');
        return;
      }

      await axios.put(`${process.env.REACT_APP_API_URL}/teams/jobsite-keywords`, {
        jobsiteKeywords: cleaned
      });
      await fetchTeamTechStacks();
      toast.success('Jobsite keywords saved. Bid links will use these for filtering and prioritization.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save jobsite keywords');
    } finally {
      setJobsiteKeywordsSaving(false);
    }
  };

  const handleCategoryToggle = (category) => {
    setBidderSettings((prev) => {
      const categories = prev.categories || [];

      if (isAllBidderCategories(categories, availableCategories)) {
        return {
          ...prev,
          categories: availableCategories.filter((c) => c !== category),
        };
      }

      if (categories.includes(category)) {
        return {
          ...prev,
          categories: categories.filter((c) => c !== category),
        };
      }

      const newCategories = [...categories, category];
      if (isAllBidderCategories(newCategories, availableCategories)) {
        return { ...prev, categories: [] };
      }

      return { ...prev, categories: newCategories };
    });
  };

  const handleLocationToggle = (location) => {
    setBidderSettings(prev => {
      const locations = prev.locations || [];
      const newLocations = locations.includes(location)
        ? locations.filter(l => l !== location)
        : [...locations, location];
      return { ...prev, locations: newLocations };
    });
  };

  const handleJobBehaviorToggle = (behavior) => {
    setBidderSettings(prev => {
      const jobBehaviors = prev.jobBehaviors || [];
      const newJobBehaviors = jobBehaviors.includes(behavior)
        ? jobBehaviors.filter(b => b !== behavior)
        : [...jobBehaviors, behavior];
      return { ...prev, jobBehaviors: newJobBehaviors };
    });
  };

  const handleTechStackToggle = (label) => {
    setBidderSettings(prev => {
      const techStacks = prev.techStacks || [];
      const next = techStacks.includes(label) ? techStacks.filter(t => t !== label) : [...techStacks, label];
      return { ...prev, techStacks: next };
    });
  };

  const handleIndividualTechStackToggle = (label) => {
    setIndividualBidderSettings(prev => {
      const techStacks = prev.techStacks || [];
      const next = techStacks.includes(label) ? techStacks.filter(t => t !== label) : [...techStacks, label];
      return { ...prev, techStacks: next };
    });
  };

  const handleSaveBidderSettings = async () => {
    try {
      setBidderSettingsLoading(true);
      
      // Clean up settings - remove "all" and "All" from arrays, keep only individual items
      const cleanedSettings = {
        categories: normalizeBidderCategories(
          bidderSettings.categories,
          availableCategories
        ),
        locations: bidderSettings.locations?.filter(l => l !== 'all') || [],
        jobBehaviors: bidderSettings.jobBehaviors?.filter(b => b !== 'All') || [],
        techStacks: bidderSettings.techStacks || []
      };
      
      await axios.put(`${process.env.REACT_APP_API_URL}/users/bidder-settings`, cleanedSettings);
      toast.success("Bidder settings saved successfully");
      setShowBidderSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save bidder settings");
    } finally {
      setBidderSettingsLoading(false);
    }
  };

  // Individual bidder settings functions
  const handleOpenIndividualBidderSettings = async (bidder) => {
    setSelectedBidder(bidder);
    setShowIndividualBidderSettings(true);
    
    try {
      setIndividualBidderSettingsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/individual-bidder-settings/${bidder._id}`);
      setIndividualBidderSettings({
        ...response.data,
        categories: normalizeBidderCategories(
          response.data.categories,
          availableCategories
        ),
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch individual bidder settings");
    } finally {
      setIndividualBidderSettingsLoading(false);
    }
  };

  const handleIndividualCategoryToggle = (category) => {
    setIndividualBidderSettings((prev) => {
      const categories = prev.categories || [];

      if (isAllBidderCategories(categories, availableCategories)) {
        return {
          ...prev,
          categories: availableCategories.filter((c) => c !== category),
        };
      }

      if (categories.includes(category)) {
        return {
          ...prev,
          categories: categories.filter((c) => c !== category),
        };
      }

      const newCategories = [...categories, category];
      if (isAllBidderCategories(newCategories, availableCategories)) {
        return { ...prev, categories: [] };
      }

      return { ...prev, categories: newCategories };
    });
  };

  const handleIndividualLocationToggle = (location) => {
    setIndividualBidderSettings(prev => {
      const locations = prev.locations || [];
      const newLocations = locations.includes(location)
        ? locations.filter(l => l !== location)
        : [...locations, location];
      return { ...prev, locations: newLocations };
    });
  };

  const handleIndividualJobBehaviorToggle = (behavior) => {
    setIndividualBidderSettings(prev => {
      const jobBehaviors = prev.jobBehaviors || [];
      const newJobBehaviors = jobBehaviors.includes(behavior)
        ? jobBehaviors.filter(b => b !== behavior)
        : [...jobBehaviors, behavior];
      return { ...prev, jobBehaviors: newJobBehaviors };
    });
  };

  const handleSaveIndividualBidderSettings = async () => {
    try {
      setIndividualBidderSettingsLoading(true);
      
      const cleanedSettings = {
        categories: normalizeBidderCategories(
          individualBidderSettings.categories,
          availableCategories
        ),
        locations: individualBidderSettings.locations?.filter(l => l !== 'all') || [],
        jobBehaviors: individualBidderSettings.jobBehaviors?.filter(b => b !== 'All') || [],
        techStacks: individualBidderSettings.techStacks || []
      };
      
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/individual-bidder-settings/${selectedBidder._id}`, 
        cleanedSettings
      );
      toast.success(`Settings for ${selectedBidder.name} saved successfully`);
      setShowIndividualBidderSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save individual bidder settings");
    } finally {
      setIndividualBidderSettingsLoading(false);
    }
  };

  const handleClearIndividualBidderSettings = async () => {
    if (!window.confirm(`Clear individual settings for ${selectedBidder.name}? They will use your master settings instead.`)) {
      return;
    }

    try {
      setIndividualBidderSettingsLoading(true);
      await axios.delete(`${process.env.REACT_APP_API_URL}/users/individual-bidder-settings/${selectedBidder._id}`);
      
      // Reset to empty settings
      setIndividualBidderSettings({
        categories: [],
        locations: [],
        jobBehaviors: [],
        techStacks: []
      });
      
      toast.success(`Individual settings cleared for ${selectedBidder.name}. They will now use master settings.`);
      setShowIndividualBidderSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to clear individual bidder settings");
    } finally {
      setIndividualBidderSettingsLoading(false);
    }
  };

  const fetchTeamData = async () => {
    try {
      if (user.role === 'lead') {
        const [teamlessRes, teamMembersRes, teamRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/teams/teamless`),
          axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team`),
          axios.get(`${process.env.REACT_APP_API_URL}/teams/all`),
        ]);
        setTeamlessUsers(teamlessRes.data);
        setTeamMembers(teamMembersRes.data);
        setTeamName(teamRes.data.find(team => team._id === user.team).name || "");
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      toast.error("Failed to fetch team data");
    }
  };

  const handleDownloadBidext = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/bidext/bidext.zip`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bidext.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Bidext extension downloaded successfully");
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Failed to download bidext extension");
    }
  };

  const handleDownloadAIInterviewAssistant = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/bidext/Mongtro.zip`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Mongtro.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Mongtro extension downloaded successfully");
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Failed to download Mongtro extension");
    }
  };

  const handleAddMember = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/teams/add-member`, {
        userId: selectedUser,
        teamId: user.team
      });
      setSelectedUser('');
      fetchTeamData();
      toast.success("Team member added successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add team member");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this team member?")) return;
    
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/teams/remove-member`, {
        memberId
      });
      fetchTeamData();
      toast.success("Team member removed successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to remove team member");
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      if (newRole === 'bidder') {
        // Show master selection dialog
        setPendingBidderId(memberId);
        setIsEditingExistingBidder(false);
        setShowMasterDialog(true);
        return;
      }
      
      await axios.put(`${process.env.REACT_APP_API_URL}/users/change-role/${memberId}`, {
        newRole
      });
      fetchTeamData();
      toast.success("Role updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update role");
    }
  };

  const handleSetMaster = async (bidderId) => {
    setPendingBidderId(bidderId);
    setIsEditingExistingBidder(true);
    setShowMasterDialog(true);
  };

  const handleMakeBidder = async () => {
    if (!selectedMaster) {
      toast.error("Please select a master");
      return;
    }
    
    try {
      if (isEditingExistingBidder) {
        // Update existing bidder's master
        await axios.put(`${process.env.REACT_APP_API_URL}/users/${pendingBidderId}/set-master`, {
          masterId: selectedMaster
        });
        toast.success("Master updated successfully");
      } else {
        // Make new bidder
        await axios.put(`${process.env.REACT_APP_API_URL}/users/change-role/${pendingBidderId}`, {
          newRole: 'bidder',
          masterId: selectedMaster
        });
        toast.success("User made bidder successfully");
      }
      
      setShowMasterDialog(false);
      setSelectedMaster('');
      setPendingBidderId(null);
      setIsEditingExistingBidder(false);
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update bidder");
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/users/change-password`, {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      toast.success("Password changed successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to change password");
    }
    setLoading(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Grid container justifyContent="center" spacing={2}>
      <Grid item xs={12} md={10} lg={8}>
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="settings tabs">
              <Tab label="Extensions" />
              <Tab label="Bidder Settings" />
              {!isBidder && <Tab label="Proxy Settings" />}
              {user.role === 'lead' && <Tab label="Team" />}
              {user.role === 'lead' && <Tab label="Jobsites" />}
              <Tab label="Password" />
              {user.role === 'lead' && <Tab label="OpenAI API Keys" />}
            </Tabs>
          </Box>

          {/* Extensions Tab */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Browser Extension
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Download the Bidext browser extension to enhance your bidding experience.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadBidext}
                    sx={{ 
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0'
                      }
                    }}
                  >
                    Download Bidext Extension
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Mongtro Extension
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Download the Mongtro browser extension for context-aware interview assistance.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadAIInterviewAssistant}
                    sx={{ 
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0'
                      }
                    }}
                  >
                    Download Mongtro Extension
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Bidder Settings Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box>
              {hasBidders && user && user.role !== 'bidder' && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    Bidder Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configure which categories, locations, and job behaviors your bidders can see.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<SettingsIcon />}
                    onClick={handleBidderSettingsClick}
                    sx={{ 
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0'
                      }
                    }}
                  >
                    Configure Bidder Settings
                  </Button>
                </Box>
              )}

              {myBidders.length > 0 && user && user.role !== 'bidder' && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    Individual Bidder Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Manage special-case filters for specific bidders. These overrides take priority over the general bidder settings above.
                  </Typography>

                  <List>
                    {myBidders.map(bidder => (
                      <ListItem key={bidder._id} divider>
                        <ListItemText 
                          primary={bidder.name}
                          secondary={`Role: ${bidder.role}`}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<SettingsIcon />}
                            onClick={() => handleOpenIndividualBidderSettings(bidder)}
                            sx={{ 
                              backgroundColor: '#4caf50',
                              '&:hover': { backgroundColor: '#45a049' }
                            }}
                          >
                            Configure
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {(!hasBidders || myBidders.length === 0) && user && user.role !== 'bidder' && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    You don't have any bidders to configure settings for.
                  </Typography>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Proxy Settings Tab */}
          {!isBidder && <TabPanel value={activeTab} index={proxyTabIndex}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Proxy Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure proxies for the Bidext extension. Add proxies here, pick a default, then use the extension header button to turn the proxy on or off.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={proxySettings.proxyFunctionalityEnabled === true}
                    onChange={(e) => handleProxyFunctionalityToggle(e.target.checked)}
                    disabled={proxySettingsLoading || proxySettingsSaving}
                  />
                }
                label="Enable proxy functionality"
                sx={{ mb: 1, display: 'block' }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                When disabled, you and your bidders cannot turn the proxy on in the Bidext extension.
              </Typography>

              {proxySettingsLoading ? (
                <Typography variant="body2" color="text.secondary">Loading proxy settings...</Typography>
              ) : proxySettings.proxyFunctionalityEnabled === false ? (
                <Typography variant="body2" color="text.secondary">
                  Proxy functionality is disabled. Enable it above to manage proxies.
                </Typography>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    Proxies
                  </Typography>
                  {proxySettings.proxies.length > 0 ? (
                    <List dense sx={{ mb: 2 }}>
                      {proxySettings.proxies.map((proxy) => (
                        <ListItem key={proxy.id} divider>
                          <ListItemText
                            primary={proxy.title}
                            secondary={`HTTP · ${proxy.host}:${proxy.port}${proxy.username ? ` (${proxy.username})` : ''}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => handleRemoveProxy(proxy.id)} aria-label="Remove proxy">
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No proxies configured yet.
                    </Typography>
                  )}

                  <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Add Proxy
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Title"
                          fullWidth
                          size="small"
                          value={newProxyForm.title}
                          onChange={(e) => setNewProxyForm((prev) => ({ ...prev, title: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          label="Proxy Server Address"
                          fullWidth
                          size="small"
                          value={newProxyForm.host}
                          onChange={(e) => setNewProxyForm((prev) => ({ ...prev, host: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Port"
                          fullWidth
                          size="small"
                          type="number"
                          value={newProxyForm.port}
                          onChange={(e) => setNewProxyForm((prev) => ({ ...prev, port: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          HTTP proxy only (e.g. Hive, Bright Data HTTP). SOCKS5 proxies are not supported.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Login"
                          fullWidth
                          size="small"
                          value={newProxyForm.username}
                          onChange={(e) => setNewProxyForm((prev) => ({ ...prev, username: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Password"
                          fullWidth
                          size="small"
                          type="password"
                          value={newProxyForm.password}
                          onChange={(e) => setNewProxyForm((prev) => ({ ...prev, password: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={handleAddProxy}
                          disabled={proxySettingsSaving}
                        >
                          {proxySettingsSaving ? 'Adding...' : 'Add Proxy'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {proxySettings.proxies.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Default Proxy
                      </Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={proxySettings.selectedProxyId || ''}
                        onChange={(e) => handleDefaultProxyChange(e.target.value)}
                        displayEmpty
                        disabled={proxySettingsSaving}
                      >
                        <MenuItem value="" disabled>Select proxy</MenuItem>
                        {proxySettings.proxies.map((proxy) => (
                          <MenuItem key={proxy.id} value={proxy.id}>{proxy.title}</MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}

                </>
              )}
            </Box>
          </TabPanel>}

          {/* Team Tab */}
          {user.role === 'lead' && (
            <TabPanel value={activeTab} index={teamTabIndex}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Team Management ({teamName})
                </Typography>

                {/* Tech Stacks Section */}
                <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Tech Stacks (Job Classification)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Define the tech stack labels your team uses for job classification. The AI will classify indexed jobs into these classes. Use these same labels in bidder settings to filter which jobs bidders see.
                  </Typography>
                  {teamTechStacks.length > 0 ? (
                    <List dense>
                      {teamTechStacks.map((s, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={s.label} secondary={s.description || '—'} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No tech stacks defined. Add some to classify jobs by your team&apos;s stack.</Typography>
                  )}
                  <Button variant="outlined" onClick={handleOpenTechStacksDialog} sx={{ mt: 1 }}>
                    {teamTechStacks.length > 0 ? 'Edit Tech Stacks' : 'Add Tech Stacks'}
                  </Button>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Select
                    fullWidth
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select user to add
                    </MenuItem>
                    {teamlessUsers.map((user) => (
                      <MenuItem key={user._id} value={user._id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    variant="contained"
                    onClick={handleAddMember}
                    disabled={!selectedUser}
                    sx={{ mt: 1 }}
                  >
                    Add to Team
                  </Button>
                </Box>

                <Typography variant="h6" gutterBottom>
                  Team Members
                </Typography>
                <List>
                  {teamMembers
                    .filter(member => member._id !== user._id && member.role === 'member')
                    .map((member) => (
                      <ListItem key={member._id}>
                        <ListItemText 
                          primary={member.name} 
                          secondary={`Role: ${member.role}`} 
                        />
                        <ListItemSecondaryAction>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleChangeRole(member._id, 'bidder')}
                            sx={{ mr: 1 }}
                          >
                            Make Bidder
                          </Button>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveMember(member._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                </List>

                <Box sx={{ my: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Bidders
                  </Typography>
                </Box>

                <List>
                  {teamMembers
                    .filter(member => member._id !== user._id && member.role === 'bidder')
                    .map((member) => {
                      // Handle both populated (object) and unpopulated (string) master field
                      const masterId = typeof member.master === 'object' 
                        ? member.master?._id 
                        : member.master;
                      const master = teamMembers.find(m => m._id === masterId);
                      return (
                        <ListItem key={member._id}>
                          <ListItemText 
                            primary={member.name} 
                            secondary={
                              <>
                                Role: {member.role}
                                {master && <br />}
                                {master && `Master: ${master.name}`}
                                {!master && <br />}
                                {!master && 'No master assigned'}
                              </>
                            } 
                          />
                          <ListItemSecondaryAction>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleSetMaster(member._id)}
                              sx={{ mr: 1 }}
                            >
                              Set Master
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleChangeRole(member._id, 'member')}
                              sx={{ mr: 1 }}
                            >
                              Make Member
                            </Button>
                            <IconButton
                              edge="end"
                              onClick={() => handleRemoveMember(member._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                </List>
              </Box>
            </TabPanel>
          )}

          {/* Jobsites Tab */}
          {user.role === 'lead' && (
            <TabPanel value={activeTab} index={jobsitesTabIndex}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Jobsite Keywords
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Define URL keywords that identify ATS/jobsite links (e.g. greenhouse.io, breezy.hr, careers.*.io).
                  Use * as a wildcard for variable URL segments. Bid links matching these keywords are prioritized and can be filtered on the Bid Links page.
                </Typography>

                <List dense>
                  {editingJobsiteKeywords.map((keyword, i) => (
                    <ListItem key={i} sx={{ gap: 1 }}>
                      <TextField
                        size="small"
                        value={keyword}
                        onChange={(e) => handleUpdateJobsiteKeyword(i, e.target.value)}
                        placeholder="e.g. greenhouse.io"
                        fullWidth
                      />
                      <IconButton edge="end" size="small" onClick={() => handleRemoveJobsiteKeyword(i)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>

                {editingJobsiteKeywords.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No jobsite keywords defined. Add keywords below to detect and prioritize ATS application links.
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                  <TextField
                    size="small"
                    label="Keyword"
                    value={newJobsiteKeyword}
                    onChange={(e) => setNewJobsiteKeyword(e.target.value)}
                    placeholder="e.g. ashbyhq.com or careers.*.io"
                    sx={{ flex: 1, minWidth: 200 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddJobsiteKeyword();
                      }
                    }}
                  />
                  <Button variant="outlined" onClick={handleAddJobsiteKeyword} disabled={!newJobsiteKeyword.trim()}>
                    Add
                  </Button>
                </Box>

                <Button variant="contained" onClick={handleSaveJobsiteKeywords} disabled={jobsiteKeywordsSaving}>
                  {jobsiteKeywordsSaving ? 'Saving...' : 'Save Jobsite Keywords'}
                </Button>
              </Box>
            </TabPanel>
          )}

          {/* Password Tab */}
          <TabPanel value={activeTab} index={passwordTabIndex}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Change Password
              </Typography>
              <form onSubmit={handlePasswordChange}>
                <TextField
                  type="password"
                  label="Current Password"
                  fullWidth
                  margin="normal"
                  value={passwords.currentPassword}
                  onChange={(e) =>
                    setPasswords((prev) => ({
                      ...prev,
                      currentPassword: e.target.value,
                    }))
                  }
                />
                <TextField
                  type="password"
                  label="New Password"
                  fullWidth
                  margin="normal"
                  value={passwords.newPassword}
                  onChange={(e) =>
                    setPasswords((prev) => ({
                      ...prev,
                      newPassword: e.target.value,
                    }))
                  }
                />
                <TextField
                  type="password"
                  label="Confirm New Password"
                  fullWidth
                  margin="normal"
                  value={passwords.confirmPassword}
                  onChange={(e) =>
                    setPasswords((prev) => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  Change Password
                </Button>
              </form>
            </Box>
          </TabPanel>

          {/* OpenAI API Keys Tab */}
          {user.role === 'lead' && (
            <TabPanel value={activeTab} index={openaiTabIndex}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  OpenAI API Key Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Configure a custom OpenAI API key for your team. If set, this key will be used instead of the global key. 
                  Leave empty to use the global key configured in the system.
                </Typography>

                {hasCustomOpenaiKey && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                    <Typography variant="body2" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>✓</span>
                      <span>Your team is currently using a custom OpenAI API key. Enter a new key below to update it, or clear the field and save to switch back to the global key.</span>
                    </Typography>
                  </Box>
                )}

                {!hasCustomOpenaiKey && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.500', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Your team is currently using the global OpenAI API key. Enter a custom key below to override it.
                    </Typography>
                  </Box>
                )}

                <TextField
                  type="password"
                  label="OpenAI API Key"
                  fullWidth
                  margin="normal"
                  value={hasCustomOpenaiKey && (!openaiApiKey || openaiApiKey.trim() === '') ? '********************************' : openaiApiKey}
                  onChange={(e) => setOpenaiApiKey(e.target.value)}
                  placeholder={hasCustomOpenaiKey ? "Enter new key to update, or clear to remove" : "Enter your OpenAI API key"}
                  helperText={hasCustomOpenaiKey ? "A custom key is currently set. Enter a new key to update, or clear and save to use the global key." : "Leave empty to use the global key"}
                  InputProps={{
                    sx: hasCustomOpenaiKey && (!openaiApiKey || openaiApiKey.trim() === '') ? {
                      '& input': {
                        color: 'text.secondary',
                        fontFamily: 'monospace',
                        letterSpacing: '2px'
                      }
                    } : {}
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveOpenaiApiKey}
                  disabled={openaiKeyLoading}
                  sx={{ mt: 2 }}
                >
                  {openaiApiKey.trim() ? 'Save API Key' : hasCustomOpenaiKey ? 'Remove Custom Key (Use Global)' : 'Save API Key'}
                </Button>
              </Box>
            </TabPanel>
          )}
        </Card>
      </Grid>

      {/* Master Selection Dialog */}
      <Dialog open={showMasterDialog} onClose={() => setShowMasterDialog(false)}>
        <DialogTitle>
          {isEditingExistingBidder ? 'Set Master for Bidder' : 'Select Master for Bidder'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isEditingExistingBidder 
              ? 'Select a team member who will be the master for this bidder. The bidder will use the master\'s resumes.'
              : 'Select a team member who will be the master for this bidder. The bidder will use the master\'s resumes.'
            }
          </Typography>
          <Select
            fullWidth
            value={selectedMaster}
            onChange={(e) => setSelectedMaster(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select master
            </MenuItem>
            {teamMembers
              .filter(member => member.role !== 'bidder')
              .map((member) => (
                <MenuItem key={member._id} value={member._id}>
                  {member.name} ({member.role})
                </MenuItem>
              ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMasterDialog(false)}>Cancel</Button>
          <Button onClick={handleMakeBidder} variant="contained">
            {isEditingExistingBidder ? 'Update Master' : 'Make Bidder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bidder Settings Dialog */}
      <Dialog 
        open={showBidderSettings} 
        onClose={() => setShowBidderSettings(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Bidder Settings
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Select which categories, locations, job behaviors, and tech stacks your bidders can see. Only selected items will be visible to bidders.
          </Typography>

          {/* Categories Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Categories
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllBidderCategories(
                      bidderSettings.categories,
                      availableCategories
                    )}
                    onChange={() => {
                      if (
                        !isAllBidderCategories(
                          bidderSettings.categories,
                          availableCategories
                        )
                      ) {
                        setBidderSettings((prev) => ({ ...prev, categories: [] }));
                      }
                    }}
                  />
                }
                label="All Categories"
              />
              <Divider sx={{ my: 1 }} />
              {availableCategories.map((category) => (
                <FormControlLabel
                  key={category}
                  control={
                    <Checkbox
                      checked={isBidderCategorySelected(
                        category,
                        bidderSettings.categories,
                        availableCategories
                      )}
                      onChange={() => handleCategoryToggle(category)}
                    />
                  }
                  label={category}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Locations Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Locations
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={availableLocations.length > 0 && 
                      bidderSettings.locations?.length === availableLocations.length &&
                      availableLocations.every(loc => bidderSettings.locations?.includes(loc))}
                    onChange={() => {
                      const allSelected = availableLocations.length > 0 && 
                        bidderSettings.locations?.length === availableLocations.length &&
                        availableLocations.every(loc => bidderSettings.locations?.includes(loc));
                      if (allSelected) {
                        setBidderSettings(prev => ({ ...prev, locations: [] }));
                      } else {
                        setBidderSettings(prev => ({ ...prev, locations: [...availableLocations] }));
                      }
                    }}
                  />
                }
                label="All Locations"
              />
              <Divider sx={{ my: 1 }} />
              {availableLocations.map((location) => (
                <FormControlLabel
                  key={location}
                  control={
                    <Checkbox
                      checked={bidderSettings.locations?.includes(location)}
                      onChange={() => handleLocationToggle(location)}
                    />
                  }
                  label={location}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Job Behaviors Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Job Behaviors
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={availableJobBehaviors.length > 0 && 
                      bidderSettings.jobBehaviors?.length === availableJobBehaviors.length &&
                      availableJobBehaviors.every(beh => bidderSettings.jobBehaviors?.includes(beh))}
                    onChange={() => {
                      const allSelected = availableJobBehaviors.length > 0 && 
                        bidderSettings.jobBehaviors?.length === availableJobBehaviors.length &&
                        availableJobBehaviors.every(beh => bidderSettings.jobBehaviors?.includes(beh));
                      if (allSelected) {
                        setBidderSettings(prev => ({ ...prev, jobBehaviors: [] }));
                      } else {
                        setBidderSettings(prev => ({ ...prev, jobBehaviors: [...availableJobBehaviors] }));
                      }
                    }}
                  />
                }
                label="All Job Behaviors"
              />
              <Divider sx={{ my: 1 }} />
              {availableJobBehaviors.map((behavior) => (
                <FormControlLabel
                  key={behavior}
                  control={
                    <Checkbox
                      checked={bidderSettings.jobBehaviors?.includes(behavior)}
                      onChange={() => handleJobBehaviorToggle(behavior)}
                    />
                  }
                  label={behavior}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Tech Stacks Section */}
          {teamTechStacks.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Tech Stacks
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Only jobs classified under selected tech stacks will be shown to bidders. Leave all unchecked to show all.
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bidderTechStackOptions.length > 0 && (!bidderSettings.techStacks?.length || bidderSettings.techStacks.length === bidderTechStackOptions.length)}
                      onChange={() => {
                        const allSelected = bidderSettings.techStacks?.length === bidderTechStackOptions.length;
                        setBidderSettings(prev => ({ ...prev, techStacks: allSelected ? [] : bidderTechStackOptions.map(s => s.label) }));
                      }}
                    />
                  }
                  label="All Tech Stacks"
                />
                <Divider sx={{ my: 1 }} />
                {bidderTechStackOptions.map((s) => (
                  <FormControlLabel
                    key={s.label}
                    control={
                      <Checkbox
                        checked={bidderSettings.techStacks?.includes(s.label)}
                        onChange={() => handleTechStackToggle(s.label)}
                      />
                    }
                    label={s.description ? `${s.label} — ${s.description}` : s.label}
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBidderSettings(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveBidderSettings} 
            variant="contained"
            disabled={bidderSettingsLoading}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Bidder Settings Dialog */}
      <Dialog 
        open={showIndividualBidderSettings} 
        onClose={() => setShowIndividualBidderSettings(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Configure Settings for {selectedBidder?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'info.main' }}>
            💡 Configure individual settings for this bidder. If no settings are selected (all empty), the bidder will use your master settings.
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Select which categories, locations, job behaviors, and tech stacks {selectedBidder?.name} can see. Only selected items will be visible to this bidder.
          </Typography>

          {/* Categories Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Categories
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllBidderCategories(
                      individualBidderSettings.categories,
                      availableCategories
                    )}
                    onChange={() => {
                      if (
                        !isAllBidderCategories(
                          individualBidderSettings.categories,
                          availableCategories
                        )
                      ) {
                        setIndividualBidderSettings((prev) => ({
                          ...prev,
                          categories: [],
                        }));
                      }
                    }}
                  />
                }
                label="All Categories"
              />
              <Divider sx={{ my: 1 }} />
              {availableCategories.map((category) => (
                <FormControlLabel
                  key={category}
                  control={
                    <Checkbox
                      checked={isBidderCategorySelected(
                        category,
                        individualBidderSettings.categories,
                        availableCategories
                      )}
                      onChange={() => handleIndividualCategoryToggle(category)}
                    />
                  }
                  label={category}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Locations Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Locations
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={availableLocations.length > 0 && 
                      individualBidderSettings.locations?.length === availableLocations.length &&
                      availableLocations.every(loc => individualBidderSettings.locations?.includes(loc))}
                    onChange={() => {
                      const allSelected = availableLocations.length > 0 && 
                        individualBidderSettings.locations?.length === availableLocations.length &&
                        availableLocations.every(loc => individualBidderSettings.locations?.includes(loc));
                      if (allSelected) {
                        setIndividualBidderSettings(prev => ({ ...prev, locations: [] }));
                      } else {
                        setIndividualBidderSettings(prev => ({ ...prev, locations: [...availableLocations] }));
                      }
                    }}
                  />
                }
                label="All Locations"
              />
              <Divider sx={{ my: 1 }} />
              {availableLocations.map((location) => (
                <FormControlLabel
                  key={location}
                  control={
                    <Checkbox
                      checked={individualBidderSettings.locations?.includes(location)}
                      onChange={() => handleIndividualLocationToggle(location)}
                    />
                  }
                  label={location}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Job Behaviors Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Job Behaviors
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={availableJobBehaviors.length > 0 && 
                      individualBidderSettings.jobBehaviors?.length === availableJobBehaviors.length &&
                      availableJobBehaviors.every(beh => individualBidderSettings.jobBehaviors?.includes(beh))}
                    onChange={() => {
                      const allSelected = availableJobBehaviors.length > 0 && 
                        individualBidderSettings.jobBehaviors?.length === availableJobBehaviors.length &&
                        availableJobBehaviors.every(beh => individualBidderSettings.jobBehaviors?.includes(beh));
                      if (allSelected) {
                        setIndividualBidderSettings(prev => ({ ...prev, jobBehaviors: [] }));
                      } else {
                        setIndividualBidderSettings(prev => ({ ...prev, jobBehaviors: [...availableJobBehaviors] }));
                      }
                    }}
                  />
                }
                label="All Job Behaviors"
              />
              <Divider sx={{ my: 1 }} />
              {availableJobBehaviors.map((behavior) => (
                <FormControlLabel
                  key={behavior}
                  control={
                    <Checkbox
                      checked={individualBidderSettings.jobBehaviors?.includes(behavior)}
                      onChange={() => handleIndividualJobBehaviorToggle(behavior)}
                    />
                  }
                  label={behavior}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Tech Stacks Section (individual) */}
          {teamTechStacks.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Tech Stacks
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bidderTechStackOptions.length > 0 && (!individualBidderSettings.techStacks?.length || individualBidderSettings.techStacks.length === bidderTechStackOptions.length)}
                      onChange={() => {
                        const allSelected = individualBidderSettings.techStacks?.length === bidderTechStackOptions.length;
                        setIndividualBidderSettings(prev => ({ ...prev, techStacks: allSelected ? [] : bidderTechStackOptions.map(s => s.label) }));
                      }}
                    />
                  }
                  label="All Tech Stacks"
                />
                <Divider sx={{ my: 1 }} />
                {bidderTechStackOptions.map((s) => (
                  <FormControlLabel
                    key={s.label}
                    control={
                      <Checkbox
                        checked={individualBidderSettings.techStacks?.includes(s.label)}
                        onChange={() => handleIndividualTechStackToggle(s.label)}
                      />
                    }
                    label={s.description ? `${s.label} — ${s.description}` : s.label}
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClearIndividualBidderSettings}
            color="warning"
            disabled={individualBidderSettingsLoading}
          >
            Clear & Use Master Settings
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setShowIndividualBidderSettings(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveIndividualBidderSettings} 
            variant="contained"
            disabled={individualBidderSettingsLoading}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tech Stacks Edit Dialog (Team lead) */}
      <Dialog open={showTechStacksDialog} onClose={() => setShowTechStacksDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Team Tech Stacks</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add labels and optional descriptions. The AI will use these to classify jobs when indexing. Example: &quot;JS/TS&quot; — &quot;React, Vue, Node.js jobs&quot;.
          </Typography>
          <List dense>
            {editingTechStacks.map((s, i) => (
              <ListItem key={i} sx={{ alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField
                    size="small"
                    label="Label"
                    value={s.label ?? ''}
                    onChange={(e) => handleUpdateTechStack(i, 'label', e.target.value)}
                    placeholder="e.g. JS/TS"
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Description"
                    value={s.description ?? ''}
                    onChange={(e) => handleUpdateTechStack(i, 'description', e.target.value)}
                    placeholder="e.g. React, Vue, Node.js jobs"
                    fullWidth
                  />
                </Box>
                <IconButton edge="end" size="small" onClick={() => handleRemoveTechStack(i)} sx={{ mt: 0.5 }}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Label"
              value={newTechStackLabel}
              onChange={(e) => setNewTechStackLabel(e.target.value)}
              placeholder="e.g. JS/TS"
              sx={{ minWidth: 120 }}
            />
            <TextField
              size="small"
              label="Description"
              value={newTechStackDescription}
              onChange={(e) => setNewTechStackDescription(e.target.value)}
              placeholder="e.g. React, Vue, Node.js jobs"
              sx={{ flex: 1, minWidth: 180 }}
            />
            <Button variant="outlined" onClick={handleAddTechStack} disabled={!newTechStackLabel.trim()}>
              Add
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTechStacksDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTechStacks} disabled={techStacksSaving}>
            {techStacksSaving ? 'Saving...' : 'Save Tech Stacks'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default Settings; 